
import React, { useEffect } from 'react';
import { useParams, useLocation } from '@tanstack/react-router';
import {
  View,
  OverlayView,
  withTranslation,
  Stack,
  Card,
  Button,
  Tag,
  Tooltip,
  Spinner,
  Message,
  Text,
  formatDate,
} from '@piwikpro/ui-components';
import { errorBoundary, withInject } from '@piwikpro/platform';
import { RouterService } from '@piwikpro/router-crate';
import { Title as TitleComponent } from '@piwikpro/title-crate';
import RequestLog from '../RequestLog';
import { ISingleDigitalGatewayReportDetail, ISingleDigitalGatewayConfiguration } from '../../interfaces';
import { SingleDigitalGatewayService } from '../../SingleDigitalGatewayService';

export interface IReportSummaryProps {
  t: (key: string, variables?: any) => string
  singleDigitalGateway: SingleDigitalGatewayService
  Title: typeof TitleComponent
  routerService: RouterService
  details: ISingleDigitalGatewayReportDetail
  appId: string
  appName: string
  configuration: ISingleDigitalGatewayConfiguration
  isLoading: boolean
  failed: boolean
}

const ReportSummary: React.FC<IReportSummaryProps> = (
  {
    Title, t, routerService, details,
    singleDigitalGateway, appId, appName, configuration,
    isLoading, failed,
  },
) => {
  const params = useParams({ strict: false });
  const { pathname } = useLocation();
  const { reportId } = params as { reportId: string };

  useEffect(() => {
    const fetchData = async () => {
      try {
        await singleDigitalGateway.getReportDetails({ appId, reportId });
        // eslint-disable-next-line no-empty
      } catch (err) { }
    };

    fetchData();
  }, []);

  if (failed) {
    return (
      <Message centerHorizontal vertical={false} condensed card message={t('apps.details.sdg.reports.get-history.failed')} />
    );
  }

  const getTag = (label?: string) => {
    switch (label) {
      case 'sent':
        return <Tag color="green">{t('apps.details.sdg.reports.status.sent')}</Tag>;
      case 'failed':
        return <Tag color="red">{t('apps.details.sdg.reports.status.failed')}</Tag>;
      case 'pending':
        return <Tag color="white">{t('apps.details.sdg.reports.status.pending')}</Tag>;
      default:
        return <Tag>{label}</Tag>;
    }
  };

  const handleClose = () => {
    singleDigitalGateway.getReports({ appId });
    routerService.push(pathname.replace(/\/o\/details\/.*$/, ''));
  };

  const onResendButtonClick = async (period: string) => {
    try {
      await singleDigitalGateway.resendReport({ appId, reportId, period });
      handleClose();
      // eslint-disable-next-line no-empty
    } catch (err) { }
  };

  const getResendButton = () => {
    let button: React.ReactElement | null = <Button appearance="simple" text={t('apps.details.sdg.resend-button.text')} onClick={() => onResendButtonClick(details.period)} />;
    if (!configuration.enabled) {
      button = (
        <Tooltip content={t('apps.details.sdg.resend-button.tooltip-sdg-disabled')}>
          <Button disabled appearance="simple" text={t('apps.details.sdg.resend-button.text')} onClick={() => onResendButtonClick(details.period)} />
        </Tooltip>
      );
    }

    if (details.status === 'pending') {
      button = null;
    }

    if (details.integration !== configuration.integration) {
      const integration = t(configuration.integration === 'acceptance' ? 'apps.details.sdg.integration-type.acceptance' : 'apps.details.sdg.integration-type.production');
      button = (
        <Tooltip content={t('apps.details.sdg.resend-button.tooltip-sdg-different-type', { integration })}>
          <Button disabled appearance="simple" text={t('apps.details.sdg.resend-button.text')} onClick={() => onResendButtonClick(details.period)} />
        </Tooltip>
      );
    }
    return (
      <Stack.Item>
        {button}
      </Stack.Item>
    );
  };

  if (isLoading) {
    return <Spinner type="fullscreen" />;
  }

  const titleContent = t('apps.details.sdg.summary.header', { period: formatDate(new Date(details.period), { format: { month: 'long', year: 'numeric' }, timeZone: 'UTC' }), app: appName });

  const detailsSection = (
    <Stack vertical spacing="wide">
      <Stack.Item fill>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.reportId')}</Text>
          </Stack.Item>
          <Stack.Item><Text as="p">{details.id}</Text></Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.period')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              {formatDate(new Date(details.period), { format: { month: 'long', year: 'numeric' }, timeZone: 'UTC' })}
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.integration-type')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              {t(details.integration === 'production'
                ? 'apps.details.sdg.integration-type.production'
                : 'apps.details.sdg.integration-type.acceptance')}
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.status')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Stack spacing={details.status === 'pending' ? 'none' : 'default'} verticalAlignment="center" horizontalAlignment="right">
              <Stack.Item>
                <div style={{ marginTop: '5px' }}>
                  {getTag(details.status)}
                </div>
              </Stack.Item>
              {getResendButton()}
            </Stack>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Stack verticalAlignment="center">
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.delivery-date')}</Text>
          </Stack.Item>
          <Stack.Item>
            <Text as="p">
              {!details.deliveredAt ? t('apps.details.sdg.reports.table.not-available') : formatDate(new Date(details.deliveredAt), { format: 'LLLL' })}
            </Text>
          </Stack.Item>
        </Stack>
      </Stack.Item>
      <Stack.Item fill>
        <Stack>
          <Stack.Item fill>
            <Text as="p" weight={600} dataId="Label">{t('apps.details.sdg.summary.details-list.sdg-ids')}</Text>
          </Stack.Item>
          <Stack.Item>
            {details.sdgIds.map(id => (<Text as="p" key={id}>{id}</Text>))}
          </Stack.Item>
        </Stack>
      </Stack.Item>
    </Stack>
  );

  return (
    <Title part={titleContent} subset>
      <OverlayView
        isOpen
        onClose={handleClose}
        title={titleContent}
      >
        <View>
          <View.Body>
            <Card>
              <Card.Section>
                <Stack vertical spacing="extraWide">
                  <Stack.Item fill>
                    <Stack
                      fullHeight
                      vertical
                      verticalAlignment="center"
                      horizontalAlignment="middle"
                    >
                      <Stack.Item fill>
                        <Text as="h3" size={16} weight={700} wrapType="noWrapAndCutText" dataId="Header.small">{t('apps.details.sdg.summary.details.header')}</Text>
                      </Stack.Item>
                      <Stack.Item fill>
                        {detailsSection}
                      </Stack.Item>
                    </Stack>
                  </Stack.Item>
                  <Stack.Item fill>
                    <RequestLog />
                  </Stack.Item>
                </Stack>
              </Card.Section>
            </Card>
          </View.Body>
        </View>
      </OverlayView>
    </Title>
  );
};

export default withInject({
  Title: 'TitleCrate.components.Title',
  routerService: 'RouterCrate.router',
  singleDigitalGateway: 'SingleDigitalGatewayCrate.singleDigitalGateway',
})(errorBoundary(withTranslation(['administration'])(ReportSummary as any)));
